import {
  type MonthCalendarContent,
  type MonthCalendarMoment,
  type MonthCalendarResponse,
} from '@opallabs/api/BFF'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { type CalendarItem } from '../types'
import { createCalendarDayFormat, type CalendarDayFormat } from './calendarDay'
import { createChipTimeFormat, type ChipTimeFormat } from './formatChipTime'

export type ResolveAccountName = (accountId: string) => string | undefined

/**
 * The page a moment or a piece of content has of its own.
 *
 * These routes take the v2 primary key, so they are built from `legacyId` and
 * never from the uuid the month is keyed by. Content names its moment as well
 * as itself, which is why content whose moment the reader cannot see has no
 * address at all.
 */
const momentHref = (workspaceSlug: string, legacyId: string) =>
  `/moments/${workspaceSlug}/${legacyId}`

const contentHref = (
  workspaceSlug: string,
  momentLegacyId: string,
  legacyId: string
) => `${momentHref(workspaceSlug, momentLegacyId)}/posts/${legacyId}`

/**
 * The moment colour arrives as a hex triplet with no `#`, because that is how
 * the moment is stored. An empty or missing value means "no colour chosen".
 */
const toCssColor = (color: string | null): string | undefined =>
  color === null || color === '' ? undefined : `#${color}`

const toMomentItem = (
  moment: MonthCalendarMoment,
  workspaceSlug: string | undefined
): CalendarItem | null => {
  if (moment.startDate === null) return null

  return {
    boardIds: moment.boardIds,
    childCount: moment.contentCount ?? undefined,
    // A moment that ends the day it starts reports no end date.
    end: moment.endDate ?? moment.startDate,
    href:
      workspaceSlug === undefined || moment.legacyId === null
        ? undefined
        : momentHref(workspaceSlug, moment.legacyId),
    id: moment.id,
    isPrivate: moment.isPrivate ?? undefined,
    kind: 'moment',
    labelIds: moment.labelIds,
    legacyId: moment.legacyId ?? undefined,
    primaryColor: toCssColor(moment.color),
    start: moment.startDate,
    statusId: moment.planningStatusId ?? undefined,
    storyId: moment.storyIds[0],
    title: moment.title ?? '',
  }
}

/**
 * The day the content sits on, in the zone the reader is in.
 *
 * Timed content follows its own instant, which is how the week view places it.
 * Content nobody chose a time for is scheduled at its moment's midnight start:
 * that instant is real but the time of day is not, so reading it in another
 * zone would drag the content a day for a reason nobody could see. It keeps the
 * day the server filed it under instead.
 *
 * `hasScheduledTime` is missing on a payload from before the endpoint reported
 * it; that reads as "the time is real", the same way the chip's time does.
 *
 * `scheduledDate` is null only for content with no schedule at all, which the
 * month has nowhere to put.
 */
const toDayKey = (
  content: MonthCalendarContent,
  toDay: CalendarDayFormat
): string | null => {
  if (content.scheduledDate === null) return null
  if (content.scheduledAt === null || content.hasScheduledTime === false)
    return content.scheduledDate

  return toDay(content.scheduledAt)
}

/**
 * The in-market window, when the content has one worth drawing.
 *
 * The takedown is a stored day and stays one — the endpoint reports no instant
 * for it — so a reader whose day has moved on can see a window a Pacific reader
 * still has a day of. A takedown on or before the day the content goes up is
 * not a window at all, so it is left off rather than drawn as a bar of zero or
 * negative length. A payload from before the endpoint reported
 * `hasOwnTakedown` reads as a takedown of the content's own.
 */
const toInMarket = (content: MonthCalendarContent, day: string) => {
  const { takedownDate } = content
  if (takedownDate === null) return undefined
  if (takedownDate <= day) return undefined

  return {
    end: takedownDate,
    start: day,
    ...(content.hasOwnTakedown === false && { followsMoment: true }),
  }
}

/**
 * The time of day the chip shows, when the content has one.
 *
 * Content nobody chose a time for is scheduled at its moment's midnight start,
 * so its instant is real but its time of day is not. `hasScheduledTime` is
 * missing on a payload from before the endpoint reported it; that reads as "the
 * time is real", which is what the month showed then.
 */
const toTime = (content: MonthCalendarContent, format: ChipTimeFormat) => {
  if (content.scheduledAt === null) return undefined
  if (content.hasScheduledTime === false) return undefined

  return format(content.scheduledAt)
}

const toContentItem = (
  content: MonthCalendarContent,
  parent: { legacyId?: string; title?: string },
  resolveAccountName: ResolveAccountName,
  format: ChipTimeFormat,
  toDay: CalendarDayFormat,
  workspaceSlug: string | undefined
): CalendarItem | null => {
  const day = toDayKey(content, toDay)
  if (day === null) return null

  const accountName =
    content.accountId === null
      ? undefined
      : resolveAccountName(content.accountId)
  const name = content.name ?? ''
  const parentLegacyId = parent.legacyId

  return {
    accountId: content.accountId ?? undefined,
    channelId: content.channelId ?? undefined,
    end: day,
    href:
      workspaceSlug === undefined ||
      parentLegacyId === undefined ||
      content.legacyId === null
        ? undefined
        : contentHref(workspaceSlug, parentLegacyId, content.legacyId),
    id: content.id,
    inMarket: toInMarket(content, day),
    isPrivate: content.isPrivate ?? undefined,
    kind: 'content',
    labelIds: content.labelIds,
    legacyId: content.legacyId ?? undefined,
    // Content is tethered to its moment, which is where its colour comes from.
    parentId: content.momentId ?? undefined,
    parentLegacyId,
    start: day,
    startsAt: content.scheduledAt ?? undefined,
    statusId: content.status ?? undefined,
    storyId: content.storyIds[0],
    // Quickfind matches the account name and the moment's title as well as
    // the content's own, because the chip shows all three, and nothing
    // downstream of here can turn an account id into a name.
    searchText: [name, accountName, parent.title]
      .filter((part) => part !== undefined && part !== '')
      .join(' '),
    time: toTime(content, format),
    title: name,
  }
}

/**
 * Turns the month document into the shape the grid draws.
 *
 * An item the calendar cannot place is left out rather than guessed at: a
 * moment with no start date and content with no scheduled date have no day to
 * sit on. They are still counted by the endpoint, so nothing here changes what
 * the workspace holds — only what this month can show.
 */
export const toCalendarItems = (
  document: MonthCalendarResponse,
  {
    locale,
    resolveAccountName = () => undefined,
    timeZone,
    workspaceSlug,
  }: {
    locale?: IETFLanguageTag
    resolveAccountName?: ResolveAccountName
    /**
     * IANA zone the reader is in, which decides the day timed content sits on.
     * Defaults to the one the browser is set to.
     */
    timeZone?: string
    /** Names the workspace in every `href`. Without it nothing has one. */
    workspaceSlug?: string
  } = {}
): CalendarItem[] => {
  const { content, moments } = document.data
  const format = createChipTimeFormat(locale)
  const toDay = createCalendarDayFormat(timeZone)

  const momentItems = moments.list.flatMap((id) => {
    const item = moments.by_id[id]
    if (item === undefined) return []
    const mapped = toMomentItem(item, workspaceSlug)
    return mapped === null ? [] : [mapped]
  })

  const contentItems = Object.values(content.by_id).flatMap((item) => {
    // A link to content names its moment as well, and only the document knows
    // the moment's own legacy id. The moment is missing when the reader can see
    // the content but not what it hangs off.
    const parent =
      item.momentId === null ? undefined : moments.by_id[item.momentId]
    const mapped = toContentItem(
      item,
      {
        legacyId: parent?.legacyId ?? undefined,
        title: parent?.title ?? undefined,
      },
      resolveAccountName,
      format,
      toDay,
      workspaceSlug
    )
    return mapped === null ? [] : [mapped]
  })

  return [...momentItems, ...contentItems]
}
